import { useState, useEffect } from 'react'

import checklistsIconBlue from '../../../assets/configuration/checklistsBlue.svg'
import TitleConfigs from '../../../components/TitleConfigs'
import Label from '../../../components/Label/label'
import Button from '../../../components/Button'
import { api } from '../../../services/api'
import Swal from 'sweetalert2'
import Table from 'rc-table'
import { Pagination, SelectPicker } from 'rsuite'
import ReactTooltip from 'react-tooltip'
import { FaInfoCircle } from 'react-icons/fa'

const PAGE_SIZE = 10
const MAX_DOWNLOAD_SIZE_BYTES = 200 * 1024 * 1024

function formatBytes(bytes) {
    if (!bytes) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    let value = bytes
    let unitIndex = 0
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex++
    }
    return `${value.toFixed(1)} ${units[unitIndex]}`
}

function getDefaultYear(files) {
    const years = new Set(files.map(file => file.year))
    const currentYear = new Date().getFullYear()
    return [currentYear, currentYear - 1].map(String).find(year => years.has(year)) || null
}

async function getBlobErrorMessage(error) {
    try {
        const body = JSON.parse(await error.response.data.text())
        return body.errors?.[0]?.msg
    } catch {
        return undefined
    }
}

export default function ExportMocs() {
    const [isExporting, setIsExporting] = useState(false)
    const [status, setStatus] = useState(null)

    const [files, setFiles] = useState([])
    const [isLoadingFiles, setIsLoadingFiles] = useState(false)

    const [selectedYear, setSelectedYear] = useState(null)
    const [selectedSite, setSelectedSite] = useState(null)
    const [page, setPage] = useState(1)

    const [selectedKeys, setSelectedKeys] = useState([])
    const [isDownloading, setIsDownloading] = useState(false)

    useEffect(() => {
        loadStatus()
        loadFiles().then(loadedFiles => setSelectedYear(getDefaultYear(loadedFiles)))
    }, [])

    useEffect(() => {
        if (!isExporting) return

        const interval = setInterval(async () => {
            const stillRunning = await loadStatus()
            if (!stillRunning) {
                clearInterval(interval)
                loadFiles()
            }
        }, 5000)

        return () => clearInterval(interval)
    }, [isExporting])

    useEffect(() => {
        setPage(1)
    }, [selectedYear, selectedSite])

    async function loadStatus() {
        try {
            let response = await api.get('/moc/export-pdf/status')
            setStatus(response.data)

            const running = response.data?.state === 'running'
            setIsExporting(running)
            return running
        } catch (error) {
            console.error(error)
            return false
        }
    }

    async function loadFiles() {
        try {
            setIsLoadingFiles(true)
            let response = await api.get('/moc/export-pdf/files')
            setFiles(response.data)
            setSelectedKeys([])
            setPage(1)
            return response.data
        } catch (error) {
            console.error(error)
            return []
        } finally {
            setIsLoadingFiles(false)
        }
    }

    async function startPdfExport() {
        const confirmationResult = await Swal.fire({
            title: "Atualizar os PDFs de todos os MOCs?",
            text: "Um PDF de cada MOC será gerado e salvo, organizado por Ano de abertura e Site. O processo roda em segundo plano no servidor e pode levar vários minutos, mesmo se você sair desta tela.",
            icon: "info",
            showCancelButton: true,
            confirmButtonColor: "#009EBE",
            cancelButtonColor: "#636e72",
            confirmButtonText: "Iniciar atualização",
            cancelButtonText: "Cancelar",
            allowOutsideClick: false,
        })
        if (!confirmationResult.isConfirmed) return

        requestExport()
    }

    async function retryFailedExport() {
        const confirmationResult = await Swal.fire({
            title: "Reprocessar os MOCs com erro?",
            text: `Os ${status.failed} MOC(s) que falharam na última atualização serão processados novamente.`,
            icon: "info",
            showCancelButton: true,
            confirmButtonColor: "#009EBE",
            cancelButtonColor: "#636e72",
            confirmButtonText: "Reprocessar",
            cancelButtonText: "Cancelar",
            allowOutsideClick: false,
        })
        if (!confirmationResult.isConfirmed) return

        requestExport({ retryFailed: true })
    }

    async function requestExport(body) {
        try {
            await api.post('/moc/export-pdf', body)
            setIsExporting(true)
            loadStatus()
        } catch (error) {
            Swal.fire({
                title: 'Erro ao iniciar atualização',
                text: error.response?.data?.errors?.[0]?.msg,
                icon: 'error',
                confirmButtonColor: "#009EBE",
            })
        }
    }

    const failedResults = status?.results?.filter(result => !result.ok) || []

    const yearOptions = [...new Set(files.map(file => file.year))]
        .sort((a, b) => b.localeCompare(a))
        .map(year => ({ label: year, value: year }))

    const siteOptions = [...new Set(files.map(file => file.site))]
        .sort((a, b) => a.localeCompare(b))
        .map(site => ({ label: site, value: site }))

    const filteredFiles = files.filter(file =>
        (!selectedYear || file.year === selectedYear) &&
        (!selectedSite || file.site === selectedSite)
    )

    const pagedFiles = filteredFiles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    const filteredKeys = filteredFiles.map(file => file.key)
    const isAllFilteredSelected = filteredKeys.length > 0 && filteredKeys.every(key => selectedKeys.includes(key))

    function toggleFileSelection(key) {
        setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])
    }

    function toggleSelectAllFiltered() {
        if (isAllFilteredSelected) {
            setSelectedKeys(prev => prev.filter(key => !filteredKeys.includes(key)))
        } else {
            setSelectedKeys(prev => [...new Set([...prev, ...filteredKeys])])
        }
    }

    function downloadSelectedFiles() {
        const selectedFiles = files.filter(file => selectedKeys.includes(file.key))
        downloadFilesAsZip(selectedFiles, 'MOCs - selecionados.zip', 'Reduza a seleção')
    }

    function downloadFilteredFiles() {
        const zipFileName = `${['MOCs', selectedYear, selectedSite].filter(Boolean).join(' - ')}.zip`
        downloadFilesAsZip(filteredFiles, zipFileName, 'Refine o filtro')
    }

    async function downloadFilesAsZip(filesToDownload, zipFileName, reduceHint) {
        if (filesToDownload.length === 0) return

        const totalSize = filesToDownload.reduce((sum, file) => sum + (file.size || 0), 0)

        if (totalSize > MAX_DOWNLOAD_SIZE_BYTES) {
            Swal.fire({
                title: 'Download muito grande',
                text: `Os ${filesToDownload.length} arquivo(s) somam ${formatBytes(totalSize)}. ${reduceHint} para até ${formatBytes(MAX_DOWNLOAD_SIZE_BYTES)} e tente novamente.`,
                icon: 'warning',
                confirmButtonColor: "#009EBE",
                confirmButtonText: 'Ok',
                allowOutsideClick: false,
            })
            return
        }

        try {
            setIsDownloading(true)
            const response = await api.post(
                '/moc/export-pdf/download',
                { keys: filesToDownload.map(file => file.key) },
                { responseType: 'blob' }
            )

            const url = URL.createObjectURL(response.data)
            const link = document.createElement('a')
            link.href = url
            link.download = zipFileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setTimeout(() => URL.revokeObjectURL(url), 1000)
        } catch (error) {
            console.error(error)
            Swal.fire({
                title: 'Erro ao baixar arquivos',
                text: await getBlobErrorMessage(error) || 'Não foi possível gerar o arquivo zip. Tente novamente.',
                icon: 'error',
                confirmButtonColor: "#009EBE",
            })
        } finally {
            setIsDownloading(false)
        }
    }

    const columnsFiles = [
        {
            title: (
                <input
                    type="checkbox"
                    checked={isAllFilteredSelected}
                    onChange={toggleSelectAllFiltered}
                />
            ),
            dataIndex: "key",
            key: "select",
            width: 40,
            render: (value) => (
                <input
                    type="checkbox"
                    checked={selectedKeys.includes(value)}
                    onChange={() => toggleFileSelection(value)}
                />
            )
        },
        { title: "Ano", dataIndex: "year", key: "year", width: 80 },
        { title: "Site", dataIndex: "site", key: "site", width: 200 },
        { title: "Arquivo", dataIndex: "fileName", key: "fileName", width: 380 },
        {
            title: "Gerado em", dataIndex: "lastModified", key: "lastModified", width: 170,
            render: (value) => new Date(value).toLocaleString('pt-BR')
        },
        {
            title: "", dataIndex: "url", key: "url", width: 80,
            render: (value) => <a href={value} target="_blank" rel="noreferrer">Abrir</a>
        },
    ]

    return (
        <>
            <TitleConfigs titulo='Exportar MOCs' icon={checklistsIconBlue} tamanho={{ width: "1.3rem" }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.4rem' }}>
                        <Button disabled={isExporting} onClick={startPdfExport}>
                            Atualizar PDFs dos MOCs
                        </Button>

                        {!isExporting && status?.failed > 0 ?
                            <Button onClick={retryFailedExport}>
                                Reprocessar MOCs com erro
                            </Button>
                            : null}

                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{
                                width: '0.6rem',
                                height: '0.6rem',
                                borderRadius: '50%',
                                display: 'inline-block',
                                backgroundColor: isExporting ? '#f1c40f' : '#2ecc71',
                            }} />
                            {isExporting ? 'Rodando' : 'Parado'}
                        </span>
                    </div>

                    {status && status.state !== 'idle' ?
                        <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            {status.processed} de {status.total} MOC(s) processado(s) — {status.done} com sucesso, {status.failed} com erro

                            {failedResults.length > 0 ?
                                <>
                                    <FaInfoCircle data-tip data-for="failedMocs" color="#009EBE" style={{ cursor: 'pointer' }} />
                                    <ReactTooltip place="right" effect="solid" id="failedMocs" clickable delayHide={200}>
                                        <div style={{ maxHeight: '300px', maxWidth: '500px', overflowY: 'auto' }}>
                                            {failedResults.map(result => (
                                                <div key={result.mocId}>
                                                    <strong>{result.codMoc || `MOC ${result.mocId}`}</strong>: {result.error}
                                                </div>
                                            ))}
                                        </div>
                                    </ReactTooltip>
                                </>
                                : null}
                        </div>
                        : null}
                </div>

                <div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', marginTop: '0.4rem' }}>
                        <div className="inputContainer" style={{ width: '200px' }}>
                            <Label htmlFor="">Ano</Label>
                            <SelectPicker
                                appearance="default"
                                style={{ width: '100%' }}
                                placeholder="Todos os anos"
                                data={yearOptions}
                                value={selectedYear}
                                onChange={setSelectedYear}
                            />
                        </div>

                        <div className="inputContainer" style={{ width: '260px' }}>
                            <Label htmlFor="">Site</Label>
                            <SelectPicker
                                appearance="default"
                                style={{ width: '100%' }}
                                placeholder="Todos os sites"
                                data={siteOptions}
                                value={selectedSite}
                                onChange={setSelectedSite}
                            />
                        </div>

                        <Button disabled={isDownloading || filteredFiles.length === 0} onClick={downloadFilteredFiles}>
                            Baixar todos do filtro {filteredFiles.length > 0 ? `(${filteredFiles.length})` : ''}
                        </Button>

                        <Button disabled={isDownloading || selectedKeys.length === 0} onClick={downloadSelectedFiles}>
                            Baixar selecionados {selectedKeys.length > 0 ? `(${selectedKeys.length})` : ''}
                        </Button>

                        {isDownloading ? <span>Gerando arquivo zip...</span> : null}
                    </div>

                    <div style={{ marginTop: '1rem' }}>
                        {isLoadingFiles ?
                            <span>Carregando...</span>
                            : filteredFiles.length === 0 ?
                                <span>Nenhum PDF exportado ainda.</span>
                                :
                                <>
                                    <Table
                                        sticky={true}
                                        columns={columnsFiles}
                                        data={pagedFiles}
                                        rowKey="key"
                                    />

                                    <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                                        <Pagination
                                            prev
                                            next
                                            first
                                            last
                                            ellipsis
                                            boundaryLinks
                                            maxButtons={5}
                                            size="sm"
                                            layout={['total', '-', 'pager']}
                                            total={filteredFiles.length}
                                            limit={PAGE_SIZE}
                                            activePage={page}
                                            onChangePage={setPage}
                                        />
                                    </div>
                                </>
                        }
                    </div>
                </div>
            </div>
        </>
    )
}
